import { ComponentProps } from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import { OpDatePicker } from 'new-components/DLS/OpDatePicker/OpDatePicker';
import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';

import { useOpFormContext } from '../../OpFormContext';
import { OpForm } from '../../OpForm';

interface OpFormDatePickerProps
  extends Pick<
      ComponentProps<typeof OpForm.Item>,
      | 'name'
      | 'label'
      | 'tooltip'
      | 'rules'
      | 'validateTrigger'
      | 'initialValue'
      | 'validateStatus' // Temp: For SUIR wrapped version
      | 'help' // Temp: For SUIR wrapped version
      | 'required' // Temp: For SUIR wrapped version
    >,
    Omit<ComponentProps<typeof OpDatePicker>, 'name'> {
  formItemClassName?: string;
  isLoading?: boolean;
  testId?: string;
}

export const OpFormDatePicker = ({
  // Form Item props
  formItemClassName,
  name,
  label,
  tooltip,
  rules,
  validateTrigger,
  initialValue,
  required, // TEMP: For old form component validation
  validateStatus, // TEMP: For old form component validation
  help, // TEMP: For old form component validation

  // For skeleton loading state
  isLoading,

  // Wrapped element props
  disabled,
  testId,

  ...elementProps
}: OpFormDatePickerProps) => {
  /** The Form is wrapped in a provider that passes the loading context
   *  to the component */
  const { isDataLoading, isReadOnly } = useOpFormContext();

  const uuid = uuidv4();
  const opDatePickerId = `op-date-picker-${uuid}`;

  return (
    <OpForm.Item
      className={clsx('op-form-date-picker', formItemClassName)}
      name={name}
      label={label}
      tooltip={tooltip}
      rules={rules}
      validateTrigger={validateTrigger}
      initialValue={initialValue}
      required={required} // TEMP: For old form component validation
      validateStatus={validateStatus} // TEMP: For old form component validation
      help={help} // TEMP: For old form component validation
      {...(!name && { htmlFor: opDatePickerId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
    >
      {isDataLoading || isLoading ? (
        /** A placeholder in the shape of the component
         * so we can immediately render the structure of the form while
         * data is loading */
        <OpSkeleton.Input active block />
      ) : (
        <OpDatePicker
          disabled={isReadOnly || disabled}
          {...(!name && { id: opDatePickerId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
          {...elementProps}
          testId={testId}
        />
      )}
    </OpForm.Item>
  );
};
